import type { FC, ReactNode } from 'react';
import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/app/providers/AuthProvider';
import { AUTH_TIMEOUTS } from '@/shared/config/constants';
import { useReturnUrlStore } from '@/shared/store/auth/useReturnUrlStore';
import { LoadingScreen } from '@/shared/ui/loading';
import { logger } from '@/shared/utils/logger';
import { paths } from '../builders/paths';

interface AuthGuardProps {
  children: ReactNode;
  /** Optional custom timeout in ms (defaults to AUTH_TIMEOUTS.ROUTE_AUTH_CHECK) */
  timeout?: number;
}

/**
 * AuthGuard - Protects routes from unauthenticated access
 *
 * Single Responsibility: Only checks if user is authenticated
 * - Shows loading screen while auth is being determined
 * - Redirects to home if not authenticated
 * - Preserves intended destination in return URL store for redirect after login
 * - Includes timeout mechanism to prevent infinite loading
 */
export const AuthGuard: FC<AuthGuardProps> = ({
  children,
  timeout = AUTH_TIMEOUTS.ROUTE_AUTH_CHECK,
}) => {
  const { isAuthenticated, isLoading } = useAuthContext();
  const location = useLocation();
  const setReturnUrl = useReturnUrlStore(state => state.setReturnUrl);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  // Timeout mechanism to prevent infinite loading when session expires
  useEffect(() => {
    if (isLoading) {
      const timeoutId = setTimeout(() => {
        logger.warn('AuthGuard: Authentication check timed out - likely expired session');
        setHasTimedOut(true);
      }, timeout);

      return () => clearTimeout(timeoutId);
    } else {
      // Reset timeout flag when loading completes
      setHasTimedOut(false);
    }
  }, [isLoading, timeout]);

  // Show loading state while auth is being determined
  if (isLoading && !hasTimedOut) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  // If authentication check timed out, redirect to home
  if (hasTimedOut) {
    logger.info('AuthGuard: Redirecting to home due to authentication timeout');
    return <Navigate to={paths.home()} replace />;
  }

  // Redirect unauthenticated users to home, preserving intended destination
  if (!isAuthenticated) {
    // Store the return URL for post-auth redirect
    const fullPath = location.pathname + location.search;
    setReturnUrl(fullPath);
    logger.info('AuthGuard: Storing return URL and redirecting to home', { returnUrl: fullPath });

    return (
      <Navigate
        to={paths.home()}
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  // User is authenticated, render children
  return children;
};
